import { zodResolver } from '@hookform/resolvers/zod';
import { createSshKeySchema, sshKeySchema } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { KeyRound, Loader2, Trash2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Confirm } from '@/components/confirm';
import { FormField } from '@/components/form-field';
import { QueryState } from '@/components/query-state';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { apiRequest } from '@/lib/api';
import { dateTime, timeAgo } from '@/lib/format';
import { applyServerErrors, errorMessage } from '@/lib/forms';

export const Route = createFileRoute('/_app/account/ssh-keys')({ component: SshKeysPage });

const keysQuery = {
  queryKey: ['account', 'ssh-keys'],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    apiRequest('/api/client/account/ssh-keys', {
      schema: z.object({ data: z.array(sshKeySchema) }),
      signal,
    }).then((r) => r.data),
};

type Values = z.input<typeof createSshKeySchema>;

function SshKeysPage() {
  const keys = useQuery(keysQuery);
  const queryClient = useQueryClient();
  const form = useForm<Values>({
    resolver: zodResolver(createSshKeySchema),
    defaultValues: { name: '', publicKey: '' },
  });
  const add = useMutation({
    mutationFn: (body: Values) =>
      apiRequest('/api/client/account/ssh-keys', { method: 'POST', body, schema: sshKeySchema }),
    onSuccess: async (k) => {
      form.reset();
      toast.success(`Added ${k.name}.`);
      await queryClient.invalidateQueries({ queryKey: keysQuery.queryKey });
    },
    onError: (e) => applyServerErrors(e, form.setError),
  });
  const remove = useMutation({
    mutationFn: (id: number) =>
      apiRequest(`/api/client/account/ssh-keys/${id}`, { method: 'DELETE', schema: z.null() }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keysQuery.queryKey }),
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle>SSH keys</CardTitle>
          <CardDescription>
            Use these to log in over SFTP without your password. Paste the contents of your{' '}
            <code>.pub</code> file, e.g. <code>~/.ssh/id_ed25519.pub</code>.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <QueryState query={keys}>
            {(list) =>
              list.length === 0 ? (
                <p className="text-muted-foreground text-sm">No keys yet.</p>
              ) : (
                <ul className="divide-y">
                  {list.map((k) => (
                    <li key={k.id} className="flex items-center gap-3 py-3">
                      <KeyRound className="text-muted-foreground size-5 shrink-0" />
                      <div className="grid min-w-0 flex-1 gap-0.5">
                        <span className="font-medium">{k.name}</span>
                        <code className="text-muted-foreground truncate text-xs">
                          {k.fingerprint}
                        </code>
                        <span className="text-muted-foreground text-xs">
                          Added {dateTime(k.createdAt)} ·{' '}
                          {k.lastUsedAt ? `last used ${timeAgo(k.lastUsedAt)}` : 'never used'}
                        </span>
                      </div>
                      <Confirm
                        title={`Remove ${k.name}?`}
                        description="SFTP logins with this key will stop working."
                        action="Remove"
                        destructive
                        onConfirm={() => remove.mutate(k.id)}
                      >
                        <Button variant="ghost" size="icon" aria-label={`Remove ${k.name}`}>
                          <Trash2 />
                        </Button>
                      </Confirm>
                    </li>
                  ))}
                </ul>
              )
            }
          </QueryState>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Add a key</CardTitle>
        </CardHeader>
        <form noValidate onSubmit={form.handleSubmit((v) => add.mutate(v))}>
          <CardContent className="grid gap-4">
            <FormField label="Name" error={form.formState.errors.name?.message}>
              <Input placeholder="MacBook" {...form.register('name')} />
            </FormField>
            <FormField label="Public key" error={form.formState.errors.publicKey?.message}>
              <Textarea
                rows={4}
                className="font-mono text-xs"
                placeholder="ssh-ed25519 AAAA… you@laptop"
                spellCheck={false}
                {...form.register('publicKey')}
              />
            </FormField>
          </CardContent>
          <CardFooter className="mt-4">
            <Button type="submit" disabled={add.isPending}>
              {add.isPending && <Loader2 className="animate-spin" />} Add key
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
